import * as Sentry from "@sentry/angular";
import type {BrowserOptions, ErrorEvent as SentryErrorEvent} from "@sentry/angular";
import {AppConfigService} from "../config/app-config.service";

/** The array form of `BrowserOptions.integrations`; the SDK also accepts a function. */
type SentryIntegrations = Extract<NonNullable<BrowserOptions['integrations']>, unknown[]>;

const SENTRY_DSN =
  'https://2b1156c89dfec19a7200637f15b7d061@o4506842072481792.ingest.us.sentry.io/4506842076479488';

/**
 * Resolves the Sentry environment tag from runtime config.
 *
 * Fetches the build-appropriate config asset — the same file `AppConfigService`
 * loads, via `AppConfigService.resolveConfigUrl()` — and returns the
 * `environment` string it contains. Falls back to `fallback` on any error:
 * network failure, non-ok HTTP status, missing key, or empty value.
 *
 * The injectable `fetchFn` parameter (defaulting to the global `fetch`)
 * makes the function unit-testable without module-level mocking.
 */
export async function resolveSentryEnvironment(
  fallback: string,
  fetchFn: typeof fetch = fetch,
): Promise<string> {
  try {
    const response = await fetchFn(AppConfigService.resolveConfigUrl());
    if (!response.ok) {
      return fallback;
    }
    const config = await response.json();
    const env = config?.environment;
    if (env && typeof env === 'string' && env.trim().length > 0) {
      return env;
    }
    return fallback;
  } catch {
    return fallback;
  }
}

/**
 * Whether Sentry should initialise for the given resolved environment.
 * Enabled for the deployed environments only — local dev (`dev` /
 * `development`) stays off. The allowlist tokens match the per-environment
 * `environment` values supplied by the hec-infra Helm values.
 */
export function shouldEnableSentry(env: string): boolean {
  return ['staging', 'pre-prod', 'production'].includes(env);
}

/**
 * Appends the resolved environment to an error event's fingerprint so
 * Sentry keeps one issue per environment (HEC-330). Without it the same
 * error from staging and production shares an issue whose title and
 * culprit come from whichever event arrived last, so the production view
 * ends up describing staging traffic. `{{ default }}` keeps the SDK's
 * default grouping and adds the environment as one more component.
 *
 * Runs as `beforeSend` rather than as a scope fingerprint because
 * `Scope.update` replaces the scope's fingerprint with a capture-level one:
 * `captureHttpError`'s explicit `['{{ default }}', status, path]` fingerprint
 * would silently drop the environment. `beforeSend` runs last for every
 * error event and sees the final fingerprint, whichever way the error was
 * captured.
 */
export function addEnvironmentToFingerprint(
  event: SentryErrorEvent,
  environment: string,
): SentryErrorEvent {
  if (environment.trim().length === 0) {
    return event;
  }
  // The SDK deletes an empty fingerprint array before beforeSend runs, so the
  // length check is defensive against a change in that behaviour.
  const fingerprint = event.fingerprint && event.fingerprint.length > 0
    ? event.fingerprint
    : ['{{ default }}'];
  event.fingerprint = fingerprint[fingerprint.length - 1] === environment
    ? fingerprint
    : [...fingerprint, environment];
  return event;
}

/**
 * The integrations `main.ts` installs: tracing, session replay and the
 * feedback widget. Constructing them starts nothing (that waits for
 * `Sentry.init`), but the Replay constructor refuses a second instance per
 * page, so a spec that builds the options more than once must inject its own
 * list rather than call this twice.
 */
function defaultSentryIntegrations(): SentryIntegrations {
  return [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      block: ['ion-button', 'ion-toolbar', 'ion-buttons']
    }),
    Sentry.feedbackIntegration({
      autoInject: false,
      showBranding: false,
      colorScheme: 'system',
      formTitle: 'Provide Feedback',
      submitButtonLabel: 'Send Feedback',
      messageLabel: 'Feedback',
      messagePlaceholder:
        'Please describe your feedback or any issue you encountered',
      successMessageText: 'Thank you for your feedback!',
      showName: false,
      showEmail: false,
      themeLight: {
        accentBackground: '#bbc303',
        accentForeground: '#000000',
      },
      themeDark: {
        accentBackground: '#bbc303',
        accentForeground: '#000000',
      },
    }),
  ];
}

/**
 * Builds the options `main.ts` hands to `Sentry.init` for a resolved
 * environment and release. Extracted from `main.ts` so the client
 * configuration can be unit-tested at the point closest to the SDK. The
 * injectable `integrations` parameter (defaulting to the real list) keeps
 * the builder callable more than once in a spec; see
 * `defaultSentryIntegrations`.
 */
export function buildSentryInitOptions(
  environment: string,
  release: string,
  integrations: SentryIntegrations = defaultSentryIntegrations(),
): BrowserOptions {
  return {
    dsn: SENTRY_DSN,
    environment,
    debug: environment === 'staging',
    enableLogs: true,
    release,
    integrations,
    // Performance Monitoring
    tracesSampleRate: 0.1,
    // Sentry's own header injection stays off. TracePropagationInterceptor
    // sends the sentry-trace header, which the reverse proxy records as the
    // sentry.trace_id span attribute — backend traces are rooted at the
    // proxy, so correlation is by attribute search, not a shared trace id.
    tracePropagationTargets: [],
    // Session Replay
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.5,
    // One Sentry issue per environment (HEC-330); see addEnvironmentToFingerprint.
    beforeSend: event => addEnvironmentToFingerprint(event, environment),
  };
}
